"use client";

import { useEffect, useRef } from "react";
import { gsap } from "@/lib/gsap";
import { Draggable } from "gsap/Draggable";
import { TestimonialsData } from "@/types/data";
import Image from "next/image";
import { batchRead } from "@/lib/helper";

if (typeof window !== "undefined") {
  gsap.registerPlugin(Draggable);
}

interface TestimonialsClientProps {
  duplicatedTestimonials: TestimonialsData["content"];
}

export default function TestimonialsClient({
  duplicatedTestimonials,
}: TestimonialsClientProps) {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const scrollerInnerRef = useRef<HTMLDivElement>(null);
  const tweenRef = useRef<gsap.core.Tween | null>(null);
  const draggableRef = useRef<Draggable[] | null>(null);
  const isHoveredRef = useRef(false);

  useEffect(() => {
    const scroller = scrollerRef.current;
    const scrollerInner = scrollerInnerRef.current;
    if (!scroller || !scrollerInner) return;

    const ctx = gsap.context(() => {
      gsap.set(scrollerRef.current, {
        opacity: 0,
        y: 50,
        scale: 0.94,
      });

      gsap.to(scrollerRef.current, {
        opacity: 1,
        y: 0,
        scale: 1,
        duration: 1,
        ease: "cubic-bezier(0.25, 0.46, 0.45, 0.94)",
        scrollTrigger: {
          trigger: scrollerRef.current,
          start: "top 80%",
          end: "top 50%",
          toggleActions: "play none none reverse",
          invalidateOnRefresh: true,
          refreshPriority: -1,
          markers: false,
        },
      });

      // Batch read to avoid forced reflow
      requestAnimationFrame(() => {
        const totalWidth = batchRead(() => {
          return scrollerInner.scrollWidth > 0
            ? scrollerInner.scrollWidth / 2
            : 0;
        });

        const speed = 25;

        if (totalWidth === 0) return;

        const startAnimation = () => {
          const currentX = gsap.getProperty(scrollerInner, "x") as number;

          let normalizedX = currentX % totalWidth;
          if (normalizedX > 0) normalizedX -= totalWidth;

          gsap.set(scrollerInner, { x: normalizedX });

          const remainingDistance = Math.abs(normalizedX + totalWidth);
          const progress = remainingDistance / totalWidth;
          const remainingTime = speed * progress;

          if (tweenRef.current) {
            tweenRef.current.kill();
          }

          tweenRef.current = gsap.to(scrollerInner, {
            x: -totalWidth,
            duration: remainingTime,
            ease: "none",
            onComplete: () => {
              gsap.set(scrollerInner, { x: 0 });
              if (!isHoveredRef.current) {
                startAnimation();
              }
            },
          });
        };

        startAnimation();

        draggableRef.current = Draggable.create(scrollerInner, {
          type: "x",
          inertia: true,
          onPress: function () {
            tweenRef.current?.kill();
          },
          onDrag: function () {
            const currentX = gsap.getProperty(scrollerInner, "x") as number;
            if (currentX > 0) {
              gsap.set(scrollerInner, { x: currentX - totalWidth });
              if (draggableRef.current) {
                draggableRef.current[0].update();
              }
            } else if (currentX < -totalWidth) {
              gsap.set(scrollerInner, { x: currentX + totalWidth });
              if (draggableRef.current) {
                draggableRef.current[0].update();
              }
            }
          },
          onDragEnd: function () {
            if (!isHoveredRef.current) {
              gsap.delayedCall(0.3, () => {
                if (!isHoveredRef.current) {
                  startAnimation();
                }
              });
            }
          },
        });

        const handleMouseEnter = () => {
          isHoveredRef.current = true;
          tweenRef.current?.pause();
        };

        const handleMouseLeave = () => {
          isHoveredRef.current = false;
          if (draggableRef.current && !draggableRef.current[0].isDragging) {
            startAnimation();
          }
        };

        scroller.addEventListener("mouseenter", handleMouseEnter);
        scroller.addEventListener("mouseleave", handleMouseLeave);

        // Cleanup function
        return () => {
          scroller.removeEventListener("mouseenter", handleMouseEnter);
          scroller.removeEventListener("mouseleave", handleMouseLeave);
        };
      });
    }, scroller);

    return () => {
      ctx.revert();
      tweenRef.current = null;
      draggableRef.current = null;
    };
  }, []);

  return (
    <div className="mt-10 scroller" data-speed="slow" ref={scrollerRef}>
      <div
        className="scroller__inner cursor-grab active:cursor-grabbing"
        ref={scrollerInnerRef}
      >
        {duplicatedTestimonials.testimonials.map((testimonial, index) => (
          <div
            key={`${testimonial.name}-${index}`}
            className="testimonial-card flex-shrink-0 w-[342px] sm:w-[420px] lg:h-[380px] bg-[#F0F4FC] rounded-2xl p-8 sm:p-10 shadow-sm"
          >
            <div className="flex flex-col h-full justify-between">
              <div>
                {duplicatedTestimonials.icon && (
                  <Image
                    src={duplicatedTestimonials.icon}
                    alt="Testimonials Icon"
                    width={60}
                    height={60}
                    quality={75}
                    loading="lazy"
                    fetchPriority="low"
                    className="object-center"
                  />
                )}

                {testimonial.text && (
                  <p className="text-[#0D0D39] mt-6 text-lg leading-relaxed">
                    {testimonial.text}
                  </p>
                )}
              </div>
              <div className="flex justify-between items-end mt-8 w-full shrink-0">
                <div className="flex items-center space-x-4 shrink-0">
                  {testimonial.avatar && (
                    <Image
                      src={testimonial.avatar}
                      alt={testimonial.avatarAlt}
                      width={48}
                      height={48}
                      quality={75}
                      fetchPriority="low"
                      className="w-12 !h-12 rounded-full object-cover shrink-0 flex-1 flex items-center justify-center"
                    />
                  )}
                  <div>
                    {testimonial.name && (
                      <div className="font-bold text-[#141E51]">
                        {testimonial.name}
                      </div>
                    )}
                    {testimonial.role && testimonial.company && (
                      <div className="text-sm text-[#474743]">
                        {testimonial.role}, {testimonial.company}
                      </div>
                    )}
                  </div>
                </div>
                {testimonial.rating && testimonial.rating > 0 && (
                  <div className="text-lg font-bold text-[#474743]">
                    {testimonial.rating}/5
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
